import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, Zap, Info, ChevronDown, Award, Search, X, TrendingUp, TrendingDown, Plus, ArrowLeft, ArrowRightLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import WalletButton from './WalletButton';
import { useWallet } from './WalletContext';
import oneInchService from './oneInchService';

const MultiCoinTracker = () => {
  const location = useLocation();
  const { isConnected, walletAddress, chainId, getProvider } = useWallet();
  const [tokenAmount, setTokenAmount] = useState(1);
  const [selectedCoins, setSelectedCoins] = useState(['ETH']);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [selectedChain, setSelectedChain] = useState('ALL');
  const [expandedDexes, setExpandedDexes] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [allPrices, setAllPrices] = useState({});
  const [gasPrices, setGasPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableTokens, setAvailableTokens] = useState({});
  const [ethPrice, setEthPrice] = useState(3300);
  
  const [showTokenSearch, setShowTokenSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortByDifference, setSortByDifference] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const searchRef = useRef(null);
  
  const [swapLoading, setSwapLoading] = useState(null);
  const [swapError, setSwapError] = useState(null);
  const [swapSuccess, setSwapSuccess] = useState(null);
  
  const [approvalStatus, setApprovalStatus] = useState({});
  const [checkingApproval, setCheckingApproval] = useState(null);
  const [approvingToken, setApprovingToken] = useState(null);
  const [approvalGasEstimate, setApprovalGasEstimate] = useState({});

  const chains = ['ETHEREUM', 'ARBITRUM', 'OPTIMISM', 'BASE'];

  const chainColors = {
    ETHEREUM: 'from-purple-500 to-purple-600',
    ARBITRUM: 'from-blue-500 to-blue-600',
    OPTIMISM: 'from-red-500 to-red-600',
    BASE: 'from-indigo-500 to-indigo-600'
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideUp {
        from { 
          opacity: 0;
          transform: translateY(20px);
        }
        to { 
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes scaleIn {
        from { 
          opacity: 0;
          transform: scale(0.95);
        }
        to { 
          opacity: 1;
          transform: scale(1);
        }
      }
      .animate-fade-in {
        animation: fadeIn 0.5s ease-out;
      }
      .animate-slide-up {
        animation: slideUp 0.6s ease-out;
      }
      .animate-scale-in {
        animation: scaleIn 0.3s ease-out;
      }
      .animation-delay-100 {
        animation-delay: 0.1s;
        opacity: 0;
        animation-fill-mode: forwards;
      }
      .animation-delay-200 {
        animation-delay: 0.2s;
        opacity: 0;
        animation-fill-mode: forwards;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    fetchAvailableTokens();
  }, []);

  useEffect(() => {
    if (selectedCoins.length > 0) {
      fetchAllCoinsPrices();
    }
  }, [selectedCoins]);

  useEffect(() => {
    const interval = setInterval(fetchAllCoinsPrices, 30000);
    return () => clearInterval(interval);
  }, [selectedCoins]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowTokenSearch(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAvailableTokens = async () => {
    try {
      const response = await fetch('/api/tokens');
      if (!response.ok) throw new Error('Failed to fetch tokens');
      const data = await response.json();
      setAvailableTokens(data.tokens);
    } catch (err) {
      console.error('Error fetching tokens:', err);
    }
  };

  const fetchAllCoinsPrices = async () => {
    try {
      setError(null);
      const pricesData = {};
      let gasData = {};
      
      for (const coin of selectedCoins) {
        try {
          const response = await fetch(`/api/prices/${coin}?real=true`, {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          
          if (!response.ok) {
            console.error(`Failed to fetch prices for ${coin}: ${response.status}`);
            continue;
          }
          
          const data = await response.json();
          pricesData[coin] = {
            prices: data.prices,
            change_24h: data.change_24h || 0,
            token: data.token
          };
          
          if (Object.keys(gasData).length === 0) {
            gasData = data.gasPrices;
          }
          
          if (coin === 'ETH' && data.prices) {
            for (const dexData of Object.values(data.prices)) {
              for (const chainData of Object.values(dexData)) {
                if (chainData.available && chainData.price) {
                  setEthPrice(chainData.price);
                  break;
                }
              }
            }
          }
        } catch (coinErr) {
          console.error(`Error fetching ${coin}:`, coinErr);
          continue;
        }
      }
      
      if (Object.keys(pricesData).length === 0) {
        throw new Error('Failed to fetch prices for any coins');
      }
      
      setAllPrices(pricesData);
      setGasPrices(gasData);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Error fetching prices:', err);
      setError('Failed to fetch prices. Please try again. Make sure the backend server is running.');
      setLoading(false);
    }
  };

  const calculateSwap = (price, fee, chain, ethPrice = 3300) => {
    const grossOutput = tokenAmount * price;
    const feeAmount = grossOutput * (fee / 100);
    const gasPrice = gasPrices[chain] || 0;
    
    let gasCostUSD = 0;
    let gasUnits = 0;
    
    if (chain === 'ETHEREUM') {
      gasUnits = 180000;
      gasCostUSD = (gasPrice * gasUnits) / 1e9 * ethPrice;
    } else if (chain === 'ARBITRUM') {
      gasUnits = 800000;
      gasCostUSD = (gasPrice * gasUnits) / 1e9 * ethPrice;
    } else if (chain === 'OPTIMISM') {
      gasUnits = 400000;
      gasCostUSD = (gasPrice * gasUnits) / 1e9 * ethPrice;
    } else if (chain === 'BASE') {
      gasUnits = 400000;
      gasCostUSD = (gasPrice * gasUnits) / 1e9 * ethPrice;
    }
    
    const total = grossOutput + feeAmount + gasCostUSD;
    
    return { grossOutput, feeAmount, gasCostUSD, total };
  };

  const getBestAndWorstDeals = (coin) => {
    const prices = allPrices[coin]?.prices;
    if (!prices) return { best: null, worst: null };
    
    let best = { dex: '', chain: '', total: Infinity };
    let worst = { dex: '', chain: '', total: 0 };
    
    Object.keys(prices).forEach(dex => {
      chains.forEach(chain => {
        const data = prices[dex][chain];
        if (data.available && data.price) {
          const swap = calculateSwap(data.price, data.fee, chain, ethPrice);
          
          if (swap.total < best.total) {
            best = { 
              dex, 
              chain, 
              total: swap.total, 
              ...swap, 
              price: data.price, 
              fee: data.fee 
            };
          }
          
          if (swap.total > worst.total) {
            worst = { 
              dex, 
              chain, 
              total: swap.total, 
              ...swap, 
              price: data.price, 
              fee: data.fee 
            };
          }
        }
      });
    });
    
    return { best: best.total !== Infinity ? best : null, worst };
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchAllCoinsPrices();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const checkTokenApproval = async (token, chain) => {
    if (token === 'ETH' || token === 'WETH') {
      return true;
    }

    const approvalKey = `${token}-${chain}`;
    setCheckingApproval(approvalKey);

    try {
      const needsApproval = await oneInchService.checkNeedsApproval(
        token,
        walletAddress,
        chain
      );
      
      setApprovalStatus(prev => ({
        ...prev,
        [approvalKey]: !needsApproval
      }));
      
      setCheckingApproval(null);
      return !needsApproval;
    } catch (error) {
      console.error('Approval check error:', error);
      setCheckingApproval(null);
      return false;
    }
  };

  const executeApproval = async (token, chain) => {
    const approvalKey = `${token}-${chain}`;
    
    if (!isConnected) {
      setSwapError('Please connect your wallet first');
      setTimeout(() => setSwapError(null), 3000);
      return;
    }

    const chainIdMap = {
      'ETHEREUM': '0x1',
      'ARBITRUM': '0xa4b1',
      'OPTIMISM': '0xa',
      'BASE': '0x2105'
    };

    const targetChainId = chainIdMap[chain];

    if (chainId !== targetChainId) {
      try {
        const provider = getProvider();
        if (!provider) {
          throw new Error('Wallet not connected');
        }
        
        setApprovingToken(approvalKey);
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: targetChainId }],
        });
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (switchError) {
        setApprovingToken(null);
        if (switchError.code === 4902) {
          setSwapError(`${chain} network not added to MetaMask`);
        } else if (switchError.code === 4001) {
          setSwapError('Chain switch rejected');
        } else {
          setSwapError(`Failed to switch to ${chain}`);
        }
        setTimeout(() => setSwapError(null), 5000);
        return;
      }
    }

    try {
      setApprovingToken(approvalKey);
      setSwapError(null);

      const approvalTx = await oneInchService.getApprovalTransaction(
        token,
        chain
      );

      const provider = getProvider();
      if (!provider) {
        throw new Error('Wallet not connected');
      }

      const txHash = await provider.request({
        method: 'eth_sendTransaction',
        params: [{
          from: walletAddress,
          to: approvalTx.to,
          data: approvalTx.data,
          value: '0x0',
        }],
      });

      console.log('Approval transaction sent:', txHash);
      
      setApprovalStatus(prev => ({
        ...prev,
        [approvalKey]: true
      }));

      setSwapSuccess({
        hash: txHash,
        chain: chain,
        message: `${token} approved! You can now swap.`
      });

      setApprovingToken(null);
      setTimeout(() => setSwapSuccess(null), 5000);

    } catch (error) {
      console.error('Approval error:', error);
      let errorMessage = 'Approval failed';
      
      if (error.code === 4001) {
        errorMessage = 'Approval rejected by user';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSwapError(errorMessage);
      setApprovingToken(null);
      setTimeout(() => setSwapError(null), 5000);
    }
  };

  const handleExecuteSwap = async (coin, chain) => {
    const fromToken = 'USDC';
    const toToken = coin;
    
    const { best } = getBestAndWorstDeals(coin);
    if (!best || best.chain !== chain) {
      setSwapError('Could not find deal information');
      setTimeout(() => setSwapError(null), 3000);
      return;
    }
    
    const usdAmount = best.total;
    
    console.log('=== EXECUTE BUY (BEST DEAL) CALLED ===');
    console.log('Buying Coin:', coin);
    console.log('From Token (spending):', fromToken);
    console.log('To Token (receiving):', toToken);
    console.log('USD Amount to spend:', usdAmount);
    console.log('Chain:', chain);
    console.log('===========================');
    
    if (isNaN(usdAmount) || usdAmount <= 0) {
      setSwapError('Invalid amount');
      setTimeout(() => setSwapError(null), 3000);
      return;
    }

    if (fromToken !== 'ETH' && fromToken !== 'WETH') {
      const approvalKey = `${fromToken}-${chain}`;
      
      if (approvalStatus[approvalKey] === undefined) {
        const isApproved = await checkTokenApproval(fromToken, chain);
        if (!isApproved) {
          return;
        }
      } else if (!approvalStatus[approvalKey]) {
        setSwapError(`Please approve ${fromToken} token first`);
        setTimeout(() => setSwapError(null), 3000);
        return;
      }
    }
    
    executeSwap(fromToken, toToken, usdAmount, chain);
  };

  const handleExecuteSell = async (coin, chain) => {
    const fromToken = coin;
    const toToken = 'USDC';
    const amount = parseFloat(tokenAmount);
    
    console.log('=== EXECUTE SELL (WORST DEAL) CALLED ===');
    console.log('Selling Coin:', coin);
    console.log('From Token (selling):', fromToken);
    console.log('To Token (receiving):', toToken);
    console.log('Amount of', fromToken, ':', amount);
    console.log('Chain:', chain);
    console.log('===========================');
    
    if (isNaN(amount) || amount <= 0) {
      setSwapError('Invalid amount');
      setTimeout(() => setSwapError(null), 3000);
      return;
    }

    if (fromToken !== 'ETH' && fromToken !== 'WETH') {
      const approvalKey = `${fromToken}-${chain}`;
      
      if (approvalStatus[approvalKey] === undefined) {
        const isApproved = await checkTokenApproval(fromToken, chain);
        if (!isApproved) {
          return;
        }
      } else if (!approvalStatus[approvalKey]) {
        setSwapError(`Please approve ${fromToken} token first`);
        setTimeout(() => setSwapError(null), 3000);
        return;
      }
    }
    
    executeSwap(fromToken, toToken, amount, chain);
  };

  const executeSwap = async (fromToken, toToken, amount, chain) => {
    const swapKey = `${fromToken}-${chain}`;
    
    if (!isConnected) {
      setSwapError('Please connect your wallet first');
      setTimeout(() => setSwapError(null), 3000);
      return;
    }

    const chainIdMap = {
      'ETHEREUM': '0x1',
      'ARBITRUM': '0xa4b1',
      'OPTIMISM': '0xa',
      'BASE': '0x2105'
    };

    const targetChainId = chainIdMap[chain];

    if (chainId !== targetChainId) {
      try {
        const provider = getProvider();
        if (!provider) {
          throw new Error('Wallet not connected');
        }
        
        setSwapLoading(swapKey);
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: targetChainId }],
        });
        
        await new Promise(resolve => setTimeout(resolve, 1000));
        
      } catch (switchError) {
        setSwapLoading(null);
        
        if (switchError.code === 4902) {
          setSwapError(`${chain} network not added to MetaMask. Please add it manually.`);
        } else if (switchError.code === 4001) {
          setSwapError('Chain switch rejected. Please switch manually.');
        } else {
          setSwapError(`Failed to switch to ${chain}. Please switch manually.`);
        }
        setTimeout(() => setSwapError(null), 5000);
        return;
      }
    }

    try {
      setSwapLoading(swapKey);
      setSwapError(null);
      setSwapSuccess(null);

      const swapTx = await oneInchService.getSwapTransaction(
        fromToken,
        toToken,
        amount,
        walletAddress,
        chain,
        1
      );

      const provider = getProvider();
      if (!provider) {
        throw new Error('Wallet not connected');
      }

      const txParams = {
        from: walletAddress,
        to: swapTx.to,
        data: swapTx.data,
        value: swapTx.value ? `0x${parseInt(swapTx.value).toString(16)}` : '0x0',
      };

      if (swapTx.gas && parseInt(swapTx.gas) > 0) {
        txParams.gas = `0x${parseInt(swapTx.gas).toString(16)}`;
      }

      console.log('Transaction params:', txParams);

      const txHash = await provider.request({
        method: 'eth_sendTransaction',
        params: [txParams],
      });

      setSwapSuccess({
        hash: txHash,
        chain: chain,
        fromToken,
        toToken,
        amount
      });
      setSwapLoading(null);

      setTimeout(() => setSwapSuccess(null), 10000);

    } catch (error) {
      console.error('Swap error:', error);
      let errorMessage = 'Swap failed';
      
      if (error.code === 4001) {
        errorMessage = 'Transaction rejected by user';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSwapError(errorMessage);
      setSwapLoading(null);
      setTimeout(() => setSwapError(null), 5000);
    }
  };

  const handleAddCoin = (tokenSymbol) => {
    if (!selectedCoins.includes(tokenSymbol)) {
      setSelectedCoins([...selectedCoins, tokenSymbol]);
    }
    setShowTokenSearch(false);
    setSearchQuery('');
  };

  const handleRemoveCoin = (tokenSymbol) => {
    if (selectedCoins.length > 1) {
      setSelectedCoins(selectedCoins.filter(c => c !== tokenSymbol));
    }
  };

  const filteredTokens = Object.entries(availableTokens)
    .filter(([symbol, token]) => {
      const query = searchQuery.toLowerCase();
      return !selectedCoins.includes(symbol) && 
             (token.name.toLowerCase().includes(query) || 
              token.symbol.toLowerCase().includes(query));
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#476bff] animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading live prices...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl p-8 max-w-md border border-gray-800">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-2">Connection Error</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <p className="text-sm text-gray-500 mb-4">
            Make sure your backend is running:<br/>
            <code className="bg-gray-800 px-2 py-1 rounded text-[#50ffff]">python api.py</code>
          </p>
          <button
            onClick={() => {
              setError(null);
              setLoading(true);
              fetchAllCoinsPrices();
            }}
            className="w-full bg-gradient-to-r from-[#476bff] to-[#50ffff] hover:opacity-90 text-white rounded-xl px-4 py-3 flex items-center justify-center gap-2 transition font-bold"
          >
            <RefreshCw className="w-4 h-4" />
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="fixed top-4 right-4 z-50">
          <WalletButton />
        </div>

        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#50ffff] hover:text-[#476bff] transition-all duration-300 mb-6 font-bold hover:gap-3 hover:translate-x-1"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Main Tracker
        </Link>

        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-4 mb-1 -mt-8 md:-mt-12 animate-fade-in">
            <img 
              src="/logo.png" 
              alt="PrecogNexus" 
              className="h-[120px] md:h-[200px] w-auto drop-shadow-[0_0_20px_rgba(80,255,255,0.4)] hover:drop-shadow-[0_0_30px_rgba(80,255,255,0.6)] transition-all duration-300" 
            />
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-white mb-2 animate-slide-up">
            MULTI-COIN DEAL FINDER
          </h1>
          <p className="text-gray-400 text-lg mb-4 animate-slide-up animation-delay-100">Compare best and worst deals across all DEXes</p>
          
          <Link 
            to="/swap"
            onClick={(e) => {
              e.preventDefault();
              setIsNavigating(true);
              setTimeout(() => {
                window.location.href = '/swap';
              }, 800);
            }}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#476bff] to-[#50ffff] hover:opacity-90 text-white rounded-xl px-6 py-3 font-bold transition shadow-lg animate-slide-up animation-delay-200"
          >
            <ArrowRightLeft className="w-5 h-5" />
            Swap Tokens
          </Link>
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl p-6 mb-6 border border-gray-800 animate-slide-up animation-delay-200">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div ref={searchRef} className="relative lg:col-span-2">
              <label className="block text-sm font-bold text-[#50ffff] mb-2">
                SELECTED COINS ({selectedCoins.length})
              </label>
              
              <div className="flex flex-wrap gap-2 mb-3">
                {selectedCoins.map(coin => {
                  const token = availableTokens[coin];
                  return (
                    <div key={coin} className="bg-black border-2 border-gray-700 rounded-lg px-3 py-2 flex items-center gap-2 hover:border-[#476bff] transition-all duration-300 hover:scale-105">
                      {token?.logo && (
                        <img src={token.logo} alt={coin} className="w-6 h-6 rounded-full" />
                      )}
                      <span className="font-bold text-white">{coin}</span>
                      {selectedCoins.length > 1 && (
                        <button
                          onClick={() => handleRemoveCoin(coin)}
                          className="text-red-400 hover:text-red-300 transition-colors duration-200 hover:scale-110"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
                
                <button
                  onClick={() => setShowTokenSearch(!showTokenSearch)}
                  className="bg-gradient-to-r from-[#476bff] to-[#50ffff] hover:opacity-90 text-white rounded-lg px-4 py-2 flex items-center gap-2 font-bold transition-all duration-300 hover:scale-105 hover:shadow-lg"
                >
                  <Plus className="w-4 h-4" />
                  Add Coin
                </button>
              </div>

              {showTokenSearch && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-gray-900 border-2 border-gray-700 rounded-xl shadow-2xl z-50 max-h-96 overflow-hidden animate-scale-in">
                  <div className="p-3 border-b border-gray-700">
                    <div className="relative">
                      <Search className="absolute left-3 top-3 w-4 h-4 text-gray-500" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search tokens..."
                        className="w-full bg-black border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white text-sm focus:ring-2 focus:ring-[#476bff] outline-none"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {filteredTokens.map(([symbol, token]) => (
                      <button
                        key={symbol}
                        onClick={() => handleAddCoin(symbol)}
                        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-800 transition-all duration-200 hover:translate-x-1"
                      >
                        {token.logo && (
                          <img src={token.logo} alt={token.symbol} className="w-8 h-8 rounded-full" />
                        )}
                        <div className="text-left flex-1">
                          <p className="font-bold text-white">{token.symbol}</p>
                          <p className="text-xs text-gray-500">{token.name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-[#50ffff] mb-2">
                AMOUNT
              </label>
              <input
                type="number"
                value={tokenAmount}
                onChange={(e) => setTokenAmount(parseFloat(e.target.value) || 0)}
                className="w-full bg-black border-2 border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-[#476bff] outline-none h-[60px] mb-3"
                placeholder="1.0"
              />
              
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="w-full bg-gradient-to-r from-[#476bff] to-[#50ffff] hover:opacity-90 text-white rounded-xl px-4 py-3 flex items-center justify-center gap-2 font-bold disabled:opacity-70 transition-all duration-300 hover:scale-105 hover:shadow-lg"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                REFRESH ALL
              </button>
            </div>
          </div>
        </div>

        
        <div className="space-y-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-black text-white">COMPARISON RESULTS</h2>
            <button
              onClick={() => setSortByDifference(!sortByDifference)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-all duration-300 ${
                sortByDifference 
                  ? 'bg-gradient-to-r from-[#476bff] to-[#50ffff] text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              <TrendingUp className="w-4 h-4" />
              Sort by Highest Difference
            </button>
          </div>
          
          {(() => {
            const coinResults = selectedCoins.map(coin => {
              const { best, worst } = getBestAndWorstDeals(coin);
              const token = availableTokens[coin];
              const coinData = allPrices[coin];
              
              if (!best || !worst) return null;

              const savings = worst.total - best.total;
              
              return { coin, best, worst, token, coinData, savings };
            }).filter(Boolean);

            if (sortByDifference) {
              coinResults.sort((a, b) => b.savings - a.savings);
            }

            return coinResults.map(({ coin, best, worst, token, coinData, savings }) => {
              const savingsPercent = ((savings / best.total) * 100).toFixed(2);

            return (
              <div key={coin} className="bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-2xl p-6 border border-gray-800 animate-slide-up hover:border-gray-700 transition-all duration-300">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-700">
                  {token?.logo && (
                    <img src={token.logo} alt={coin} className="w-12 h-12 rounded-full" />
                  )}
                  <div className="flex-1">
                    <h2 className="text-2xl font-black text-white">{coin}</h2>
                    <p className="text-gray-400">{token?.name}</p>
                  </div>
                  {coinData?.change_24h !== 0 && (
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-lg ${coinData.change_24h >= 0 ? 'bg-green-900/30' : 'bg-red-900/30'}`}>
                      {coinData.change_24h >= 0 ? <TrendingUp className="w-4 h-4 text-green-400" /> : <TrendingDown className="w-4 h-4 text-red-400" />}
                      <span className={`text-sm font-bold ${coinData.change_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {Math.abs(coinData.change_24h).toFixed(2)}%
                      </span>
                    </div>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-gradient-to-r from-green-900/30 to-green-800/30 border-2 border-green-500 rounded-xl p-5 hover:shadow-green-500/20 hover:shadow-xl transition-all duration-300 hover:scale-105">
                    <div className="flex items-center gap-2 mb-3">
                      <Award className="w-5 h-5 text-green-400" />
                      <span className="text-sm font-bold text-green-400">BEST DEAL</span>
                    </div>
                    <p className="text-2xl font-black text-white mb-1">{best.dex}</p>
                    <p className="text-green-400 font-semibold mb-3">{best.chain}</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Price:</span>
                        <span className="font-bold text-white">${best.price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Fee:</span>
                        <span className="font-bold text-white">${best.feeAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Gas Cost:</span>
                        <span className="font-bold text-white">${best.gasCostUSD.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-green-700">
                        <span className="text-green-400 font-bold">Total Cost:</span>
                        <span className="text-xl font-black text-green-400">
                          ${best.total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </span>
                      </div>
                    </div>
                    
                    {(() => {
                      const fromToken = 'USDC';
                      const approvalKey = `${fromToken}-${best.chain}`;
                      const needsApproval = fromToken !== 'ETH' && fromToken !== 'WETH' && !approvalStatus[approvalKey];
                      const isChecking = checkingApproval === approvalKey;
                      const isApproving = approvingToken === approvalKey;
                      const isSwapping = swapLoading === `${coin}-${best.chain}`;
                      
                      if (!isConnected) {
                        return (
                          <button
                            disabled
                            className="mt-4 w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 bg-gray-700 text-gray-400 cursor-not-allowed"
                          >
                            <Zap className="w-4 h-4" />
                            Connect Wallet to Swap
                          </button>
                        );
                      }
                      
                      if (isChecking) {
                        return (
                          <button
                            disabled
                            className="mt-4 w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 bg-gray-600 text-white cursor-wait"
                          >
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Checking approval...
                          </button>
                        );
                      }
                      
                      if (needsApproval) {
                        return (
                          <>
                            <button
                              onClick={() => executeApproval(fromToken, best.chain)}
                              disabled={isApproving}
                              className={`mt-4 w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-all duration-300 ${
                                isApproving
                                  ? 'bg-orange-600 text-white cursor-wait'
                                  : 'bg-orange-500 hover:bg-orange-600 text-white hover:scale-105'
                              }`}
                            >
                              {isApproving ? (
                                <>
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                  Approving...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                  </svg>
                                  Approve {fromToken} Token
                                </>
                              )}
                            </button>
                            <p className="text-xs text-orange-400 mt-2 text-center">
                              ⚠️ One-time approval needed (~
                              {best.chain === 'ETHEREUM' ? '$5-30' : 
                               best.chain === 'ARBITRUM' ? '$0.03-$1' :
                               best.chain === 'OPTIMISM' ? '$0.05-$1' :
                               best.chain === 'BASE' ? '$0.01-$0.50' : '$0.50'} gas)
                            </p>
                          </>
                        );
                      }
                      
                      return (
                        <>
                          <button
                            onClick={() => handleExecuteSwap(coin, best.chain)}
                            disabled={isSwapping}
                            className={`mt-4 w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-all duration-300 ${
                              isSwapping
                                ? 'bg-green-600 text-white cursor-wait'
                                : 'bg-green-500 hover:bg-green-600 text-white hover:scale-105'
                            }`}
                          >
                            {isSwapping ? (
                              <>
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                Executing...
                              </>
                            ) : (
                              <>
                                <ArrowRightLeft className="w-4 h-4" />
                                Execute Best Deal
                              </>
                            )}
                          </button>
                          {fromToken !== 'ETH' && fromToken !== 'WETH' && approvalStatus[approvalKey] && (
                            <p className="text-xs text-green-400 mt-2 text-center">
                              ✅ Approved • Ready to swap
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  <div className="bg-gradient-to-r from-red-900/30 to-red-800/30 border-2 border-red-500 rounded-xl p-5 hover:shadow-red-500/20 hover:shadow-xl transition-all duration-300 hover:scale-105">
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingDown className="w-5 h-5 text-red-400" />
                      <span className="text-sm font-bold text-red-400">WORST DEAL</span>
                    </div>
                    <p className="text-2xl font-black text-white mb-1">{worst.dex}</p>
                    <p className="text-red-400 font-semibold mb-3">{worst.chain}</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Price:</span>
                        <span className="font-bold text-white">${worst.price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Fee:</span>
                        <span className="font-bold text-white">${worst.feeAmount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Gas Cost:</span>
                        <span className="font-bold text-white">${worst.gasCostUSD.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-red-700">
                        <span className="text-red-400 font-bold">Total Cost:</span>
                        <span className="text-xl font-black text-red-400">
                          ${worst.total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleExecuteSell(coin, worst.chain)}
                      disabled={!isConnected}
                      className={`mt-4 w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-all duration-300 ${
                        !isConnected
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          : 'bg-red-500 hover:bg-red-600 text-white hover:scale-105'
                      }`}
                    >
                      <ArrowRightLeft className="w-4 h-4" />
                      {!isConnected ? 'Connect Wallet to Sell' : 'Sell Coins'}
                    </button>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-[#c5d434] to-[#50ffff] rounded-xl p-4 text-black hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold opacity-80">DIFFERENCE</p>
                      <p className="text-xs opacity-70">Between best and worst deal</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-black">${savings.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })})()}
        </div>

        <div className="mt-6 bg-gradient-to-r from-gray-900 to-black border border-[#476bff] rounded-xl p-4">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-[#476bff] flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-300">
              <p className="font-bold mb-2 text-white">HOW IT WORKS:</p>
              <ul className="space-y-1 text-xs">
                <li>• Select multiple coins to compare deals across all DEXes and chains</li>
                <li>• Best deal = lowest total cost (amount + fees + gas)</li>
                <li>• Worst deal = highest total cost (amount + fees + gas)</li>
                <li>• Difference shows the cost variation between best and worst deals</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {isNavigating && (
        <div className="min-h-screen bg-black flex items-center justify-center fixed inset-0 z-50">
          <div className="text-center">
            <RefreshCw className="w-12 h-12 text-[#476bff] animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Navigating to Swap...</p>
          </div>
        </div>
      )}

      {swapSuccess && (
        <div className="fixed bottom-8 right-8 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl shadow-2xl p-6 max-w-md z-50 animate-slide-up">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-green-200 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-1">Swap Successful! 🎉</h3>
              <p className="text-sm text-green-100 mb-2">
                Swapped {swapSuccess.amount} {swapSuccess.fromToken} → {swapSuccess.toToken} on {swapSuccess.chain}
              </p>
              <a
                href={`https://${swapSuccess.chain === 'ETHEREUM' ? 'etherscan.io' : 
                      swapSuccess.chain === 'ARBITRUM' ? 'arbiscan.io' : 
                      swapSuccess.chain === 'OPTIMISM' ? 'optimistic.etherscan.io' : 
                      'basescan.org'}/tx/${swapSuccess.hash}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-green-200 hover:text-white underline"
              >
                View on Explorer →
              </a>
            </div>
            <button
              onClick={() => setSwapSuccess(null)}
              className="text-green-200 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {swapError && (
        <div className="fixed bottom-8 right-8 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl shadow-2xl p-6 max-w-md z-50 animate-slide-up">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-200 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-1">Swap Failed</h3>
              <p className="text-sm text-red-100">{swapError}</p>
            </div>
            <button
              onClick={() => setSwapError(null)}
              className="text-red-200 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiCoinTracker;